import styled, { keyframes } from 'styled-components';
import { FaSpinner } from 'react-icons/fa';
import { Link } from 'react-router-dom';

export const SidebarContainer = styled.aside<{ collapsed: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${({ collapsed }) => (collapsed ? '70px' : '450px')};
  background: #521eab;
  color: #c2a0f8;
  transition: width 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;

  /* Scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #521eab;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #6e40bd;
    border-radius: 4px;
  }
`;

export const LogoWrapper = styled.div`
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid #6e40bd;

  img {
    height: 30px;
    max-width: 100%;
    transition: all 0.3s ease;
  }
`;

export const InputArea = styled.label<{ collapsed?: boolean }>`
  padding: 12px 8px;
  position: relative;
  display: flex;
  align-items: center;

  svg:first-child {
    position: absolute;
    left: 28px;
    color: #c2a0f8;
    cursor: text;
    outline: none;
    transition: 0.3s ease;
  }

  svg:last-child {
    position: absolute;
    right: 28px;
    color: #c2a0f8;
    cursor: pointer;
    outline: none;
    transition: 0.3s ease;
  }
`;

export const SearchInput = styled.input<{ collapsed?: boolean }>`
  width: 100%;
  padding: 8px ${({ collapsed }) => (collapsed ? '8px' : '12px')} 8px
    ${({ collapsed }) => (collapsed ? '8px' : '40px')};
  border: none;
  border-radius: 4px;
  background: #6e40bd;
  color: #fcfcfe;

  cursor: text;

  &::placeholder {
    color: #c2a0f8;
  }
`;

export const SidebarSectionLabel = styled.span<{ fontSize?: string }>`
  display: block;
  margin: 16px 0 8px 12px;
  font-size: ${({ fontSize }) => fontSize || '16px'};
  font-weight: 500;
  color: #fff;
  text-transform: uppercase;
`;

export const SidebarSectionLabelWithSeparator = styled.div<{
  fontSize?: string;
}>`
  display: flex;
  align-items: center;
  margin: 16px 0 8px 12px;

  span {
    font-size: ${({ fontSize }) => fontSize || '16px'};
    font-weight: 500;
    color: #fff;
    text-transform: uppercase;
    margin-right: 12px;
    white-space: nowrap;
  }

  .hr-separator {
    flex: 1;
    height: 1px;
    background: #6e40bd;
    border: none;
    min-width: 30px;
  }
`;

export const MenuItem = styled.li<{ collapsed?: boolean }>`
  list-style: none;
`;

export const MenuLink = styled(Link)<{ fontSize?: string }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 16px 10px 24px;
  background: none;
  border: none;
  color: #fff !important;
  cursor: pointer;
  gap: 8px;
  text-align: start;
  font-size: ${({ fontSize }) => fontSize || '14px'};
  font-weight: 500;
  color: inherit;
  text-decoration: inherit;
  position: relative;

  &:hover {
    background: #6e40bd;
    color: #fff;
  }

  svg {
    flex-shrink: 0;
  }

  .favorite-indicator {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-bottom: 10px solid #8b5fbf;
    pointer-events: all;
  }

  .actions-button {
    background: none;
    border: none;
    color: #c2a0f8;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s;
    margin-left: auto;
    position: relative;

    &:hover {
      background: #8b5fbf;
      color: #fff;
    }
  }

  .dropdown {
    position: absolute;
    top: 100%;
    right: 8px;
    background: #6e40bd;
    border: 1px solid #8b5fbf;
    border-radius: 4px;
    min-width: 150px;
    z-index: 1001;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    color: #fff;
    cursor: pointer;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    font-size: 14px;

    &:hover {
      background: #8b5fbf;
    }
  }
`;

export const LeftBar = styled.div<{
  collapsed: boolean;
}>`
  width: ${({ collapsed }) => (collapsed ? ' 100%' : 'auto')};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  align-items: center;
  flex: 0 0 auto;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  /* Scrollbar personalizada para a LeftBar - mais fina */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(110, 64, 189, 0.6);
    border-radius: 2px;

    &:hover {
      background-color: #6e40bd;
    }
  }

  /* Para Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(110, 64, 189, 0.6) transparent;
`;

export const RightBar = styled.div`
  flex: 1;
  padding: 8px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #6e40bd;
    border-radius: 4px;
  }
`;

export const IconButton = styled.button<{
  selected: boolean;
}>`
  width: 100%;
  background: ${({ selected }) => (selected ? '#6e40bd' : 'transparent')};
  border: none;
  color: #fff;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;

  &:hover {
    background: #6e40bd;
  }
`;

export const ModuloIcon = styled.div`
  width: 20px;
  height: 20px;
  background: #4b5563;
  border-radius: 50%;
`;

export const ModuloLabel = styled.span<{
  collapsed: boolean;
}>`
  font-size: 14px;
  display: ${({ collapsed }) => (collapsed ? 'none' : 'block')};
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Spinner = styled(FaSpinner)`
  animation: ${spin} 1s linear infinite;
`;

export const ScrollContainer = styled.div`
  flex: 1;
  overflow: hidden; /* Mudança aqui */
  display: flex; /* Adicione esta linha */

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #6e40bd;
    border-radius: 4px;
  }
`;

export const VerticalSeparator = styled.div`
  width: 1px;
  background: #6e40bd;
  height: 100%;
  flex-shrink: 0;
`;
